/**
 * Normalize the many `device_type` strings the backend may emit into a small
 * canonical set so the COP dashboard does not split a single physical bucket
 * (e.g. "Phone") across multiple visual rows ("Phone" + "Cellphone").
 *
 * The backend currently emits at least: Phone, Cellphone, Smartphone,
 * SamsungPhone, Watch, Vehicle, Drone. Pathfinder already collapses the phone
 * variants in several places; this util gives COP the same behavior.
 *
 * Returns a Capitalized canonical string ("Phone", "Watch", "Vehicle",
 * "Drone", "Wardriver") so the LiveStatusDashboard's per-device-type
 * sub-rows render with a consistent label. Unknown types fall through
 * with their first letter capitalized to match the existing display
 * formatting.
 */

const PHONE_ALIASES = new Set([
  'phone',
  'cellphone',
  'cell_phone',
  'cell-phone',
  'smartphone',
  'samsungphone',
  'samsung_phone',
  'mobile',
]);

const WATCH_ALIASES = new Set([
  'watch',
  'smartwatch',
  'smart_watch',
  'applewatch',
  'apple_watch',
]);

const VEHICLE_ALIASES = new Set([
  'vehicle',
  'beacon',
  'car',
]);

const DRONE_ALIASES = new Set([
  'drone',
  'uas',
  'uav',
  'aircraft',
]);

const WARDRIVER_ALIASES = new Set([
  'wardriver',
  'beluga',
  'rf_collector',
  'rf-collector',
]);

/** "wardriver" contains "watch" — never classify a Beluga as a watch. */
export function isWardriverType(raw: string | null | undefined): boolean {
  const lower = (raw || '').trim().toLowerCase();
  return !!lower && (WARDRIVER_ALIASES.has(lower) || lower.includes('wardriver') || lower.includes('beluga'));
}

/**
 * Normalize a single device_type string. Returns null for null/undefined
 * input so callers can preserve absence (versus mapping to "Phone").
 */
export function normalizeDeviceType(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const lower = raw.trim().toLowerCase();
  if (!lower) return null;
  if (isWardriverType(lower)) return 'Wardriver';
  if (PHONE_ALIASES.has(lower)) return 'Phone';
  if (WATCH_ALIASES.has(lower)) return 'Watch';
  if (VEHICLE_ALIASES.has(lower)) return 'Vehicle';
  if (DRONE_ALIASES.has(lower)) return 'Drone';
  // Unknown - title-case it so it still renders cleanly.
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Apply normalization to a single device-shaped record. The function returns
 * a NEW object only if normalization actually changed `device_type`, to keep
 * referential equality stable for React memoization in the common case where
 * the backend already sent a canonical value.
 */
export function normalizeDeviceLocation<T extends { device_type?: string | null }>(d: T): T {
  const normalized = normalizeDeviceType(d.device_type ?? null);
  if (normalized === (d.device_type ?? null)) return d;
  return { ...d, device_type: normalized ?? undefined } as T;
}

/**
 * Apply `normalizeDeviceLocation` to an array. Returns the same array
 * reference if nothing changed (cheap O(n) scan with early exit) so callers
 * get cheap re-renders when the backend was already canonical.
 */
export function normalizeDeviceLocations<T extends { device_type?: string | null }>(devices: T[]): T[] {
  let changed = false;
  const next: T[] = new Array(devices.length);
  for (let i = 0; i < devices.length; i++) {
    const d = devices[i];
    const n = normalizeDeviceLocation(d);
    if (n !== d) changed = true;
    next[i] = n;
  }
  return changed ? next : devices;
}
